<script setup lang="ts">
import type { WorkingGroup } from '#design-tokenomics/composables/useWorkingGroups'
import type { ColumnDef } from '@forgd/ui-kit/blocks/data-table'
import { useNumberFieldBlurGuard } from '#design-tokenomics/composables/useNumberFieldBlurGuard'
import { formatValue } from '@forgd/formatter'
import { ColumnHeader, DataTable, InputCell } from '@forgd/ui-kit/blocks/data-table'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@forgd/ui-kit/input-group'
import { NumberField, NumberFieldInput } from '@forgd/ui-kit/number-field'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@forgd/ui-kit/select'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline'

const model = defineModel<WorkingGroup[]>({ required: true })

function commit() {
  model.value = [...model.value]
}

const costBasisFormat = { minimumFractionDigits: 2, maximumFractionDigits: 2 }
const costBasisGuard = useNumberFieldBlurGuard(costBasisFormat)

const typeOfSellPressureOptions = [
  { label: 'Aggressive', value: 'aggressive' },
  { label: 'Moderate', value: 'moderate' },
  { label: 'Conservative', value: 'conservative' },
  { label: 'Not a seller', value: 'none' },
]

const columns: ColumnDef<WorkingGroup>[] = [
  {
    accessorKey: 'name',
    size: 200,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Group Name/Allocation %', class: 'pr-1 pl-2' }),
    cell: ({ row, column }) => h(InputCell, { column, class: 'flex items-center gap-2' }, () => [
      h('div', { class: 'h-10 w-1 rounded-full', style: `background-color: ${row.original.color}` }),
      h('div', {}, [
        h('div', {}, row.original.name),
        h('div', { class: 'text-xs font-semibold' }, `${row.original.allocationPercentage}%`),
      ]),
    ]),
  },
  {
    accessorKey: 'costBasisTokens',
    size: 150,
    maxSize: 150,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Cost Basis Tokens', class: 'px-1' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => h(NumberField, {
      'modelValue': row.original.costBasisTokens,
      'min': 0,
      'formatOptions': costBasisFormat,
      'stepSnapping': false,
      'onUpdate:modelValue': (value: number | undefined) => {
        if (!costBasisGuard.accepts(value, row.original.costBasisTokens)) {
          return
        }
        row.original.costBasisTokens = value !== undefined && Number.isFinite(value) ? value : 0
        commit()
      },
    }, () => h(InputGroup, { size: 'sm' }, () => [
      h(InputGroupAddon, () => '$'),
      h(NumberFieldInput, { ...costBasisGuard.inputListeners, 'aria-label': 'Cost Basis Tokens' }),
    ]))),
  },
  {
    accessorKey: 'impliedFdv',
    size: 150,
    maxSize: 150,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Implied FDV', class: 'px-1' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => h(InputGroup, { size: 'sm', disabled: true }, () => [
      h(InputGroupAddon, () => '$'),
      h(InputGroupInput, { 'modelValue': formatValue(row.original.impliedFdv ?? 0, 'number:0'), 'aria-label': 'Implied FDV' }),
    ])),
  },
  {
    accessorKey: 'typeOfSellPressure',
    size: 170,
    maxSize: 170,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Type of Sell Pressure', class: 'px-1' }),
    cell: ({ row, column }) => h(InputCell, { column }, () => h(Select<string>, {
      'modelValue': row.original.typeOfSellPressure,
      'onUpdate:modelValue': (value) => {
        row.original.typeOfSellPressure = value
        commit()
      },
    }, () => [
      h(SelectTrigger, { size: 'sm' }, () => h(SelectValue)),
      h(SelectContent, () => typeOfSellPressureOptions.map(({ value, label }) =>
        h(SelectItem, { value }, () => label),
      )),
    ])),
  },
  {
    id: 'sourceOfSellPressure',
    size: 220,
    maxSize: 220,
    header: ({ column }) => h(ColumnHeader, { column, label: 'Potential Source of Sell Pressure', class: 'pr-2 pl-1' }),
    cell: ({ row, column }) => {
      const hasPressure = !!row.original.typeOfSellPressure && row.original.typeOfSellPressure !== 'none'
      return h(InputCell, { column }, () => h('div', { class: `flex h-8 items-center gap-2 rounded-lg px-2 ${hasPressure ? 'bg-green-100' : 'bg-red-100'}` }, [
        h(hasPressure ? CheckCircleIcon : XCircleIcon, { 'class': `size-4 shrink-0 ${hasPressure ? 'text-green-600' : 'text-red-500'}`, 'aria-hidden': 'true' }),
        hasPressure ? 'Yes' : 'No',
      ]))
    },
  },
]
</script>

<template>
  <DataTable :columns="columns" :data="model" size="md" />
</template>
